import React, { useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

// Material
import { makeStyles } from "@material-ui/core/styles";

// Config
import { getRoute, urls } from "../../config/routes";
import { useAppInsights } from "../../utils/telemetry/AppInsights";
import isCaseLocked from "../../utils/isCaseLocked";

// Actions
import {
  getMedia,
  getMediaItem,
  deleteMedia,
  clearMediaError
} from "../../state/media/actions";
import {
  getMedia as getMediaPerpetrators,
  getMediaItem as getMediaItemPerpetrator,
  deleteMedia as deleteMediaPerpetrator,
  clearMediaError as clearMediaErrorPerpetrator
} from "../../state/mediaPerpetrators/actions";

// Components
import CaseMediaWhatsNew from "../CaseMediaWhatsNew";
import LoadingSection from "../LoadingSection";
import MediaItemList from "../MediaItemList";

const useStyles = makeStyles(theme => ({
  content: {
    padding: theme.spacing(5),
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "wrap"
  }
}));

const CaseMediaPanel = () => {
  const { caseId } = useParams();
  const { currentCase, data, isLoading, isDeleting, error } = useSelector(
    state => state.media
  );
  const mediaPerpetrators = useSelector(state => state.mediaPerpetrators);
  const { data: caseData } = useSelector(s => s.caseDetail);
  const caseLocked = isCaseLocked(caseData);
  const { trackException } = useAppInsights();
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    return () => {
      dispatch(clearMediaError());
      dispatch(clearMediaErrorPerpetrator());
    };
  }, [dispatch]);

  useEffect(() => {
    if ((currentCase !== caseId || !data) && !error && !isLoading) {
      dispatch(
        getMedia({
          caseId,
          onFail: () =>
            trackException(`Failed to get media from case: ${caseId}`)
        })
      );
    }
  }, [caseId, currentCase, data, dispatch, error, isLoading, trackException]);

  useEffect(() => {
    if (
      (mediaPerpetrators.currentCase !== caseId || !mediaPerpetrators.data) &&
      !mediaPerpetrators.error &&
      !mediaPerpetrators.isLoading
    ) {
      dispatch(
        getMediaPerpetrators({
          caseId,
          onFail: () =>
            trackException(`Failed to get media from case: ${caseId}`)
        })
      );
    }
  }, [
    mediaPerpetrators.caseId,
    mediaPerpetrators.currentCase,
    mediaPerpetrators.data,
    dispatch,
    mediaPerpetrators.error,
    mediaPerpetrators.isLoading,
    trackException
  ]);

  const handleDelete = record => dispatch(deleteMedia(record));
  const handleDeletePerpetrator = record =>
    dispatch(deleteMediaPerpetrator(record));

  const handleFetchImage = mediaId => () => {
    dispatch(
      getMediaItem({
        caseId,
        mediaId,
        onFail: () =>
          trackException(
            `Failed to get media item: ${mediaId} at case: ${caseId}`
          )
      })
    );
  };

  const handleFetchPerpetratorImage = mediaId => () => {
    dispatch(
      getMediaItemPerpetrator({
        caseId,
        mediaId,
        onFail: () =>
          trackException(
            `Failed to get media item: ${mediaId} at case: ${caseId}`
          )
      })
    );
  };

  const handleSelect = itemId => () => {
    history.push(getRoute(urls.caseMediaDetail, { caseId, itemId }));
  };

  return (
    <>
      {isLoading || mediaPerpetrators.isLoading ? (
        <div className={classes.content}>
          <LoadingSection normalHeight noBackground />
        </div>
      ) : (
        <div className={classes.content}>
          {data && (
            <MediaItemList
              caseId={caseId}
              data={data}
              mediaItemProps={{ isDeleting, showDelete: !caseLocked }}
              onDelete={handleDelete}
              onFetchImage={handleFetchImage}
              onSelect={handleSelect}
              page="mediaPage"
              disablePlaceholder={
                mediaPerpetrators.data && mediaPerpetrators.data.length > 0
              }
            />
          )}
          {mediaPerpetrators.data && (
            <MediaItemList
              caseId={caseId}
              data={mediaPerpetrators.data}
              mediaItemProps={{
                isDeleting: mediaPerpetrators.isDeleting,
                showDelete: !caseLocked,
                isPerpetrator: true
              }}
              onDelete={handleDeletePerpetrator}
              onFetchImage={handleFetchPerpetratorImage}
              onSelect={handleSelect}
              page="mediaPage"
              disablePlaceholder
              displayEncourage={data && data.length > 0}
            />
          )}
        </div>
      )}
      {data && <CaseMediaWhatsNew defaultClosed={data.length > 0} />}
    </>
  );
};

export default CaseMediaPanel;
